import { PageLimitFilter } from '@typings';
import { TableColumnType } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type ColumnOrder = {
  column: string;
  inverse: boolean;
};

export function usePaginationFilter<T>(pageSize = 20) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderColumn, setOrderColumn] = useState<ColumnOrder | null>(null);

  useEffect(() => {
    if (!orderColumn) {
      setCurrentFilter(prevFilter => ({
        ...prevFilter,
        order: undefined,
      }));
      return;
    }
    setCurrentFilter(prevFilter => ({
      ...prevFilter,
      order: `[${orderColumn.column},${orderColumn.inverse ? 'DESC' : 'ASC'}]`,
    }));
  }, [orderColumn]);

  const tableColumnSortOrderSymbol = useMemo<
    Record<string, 'ascend' | 'descend'>
  >(() => {
    if (!orderColumn) return {};
    return {
      [orderColumn.column]: orderColumn?.inverse ? 'ascend' : 'descend',
    };
  }, [orderColumn]);

  const currentFilter: PageLimitFilter = useMemo(
    () => ({
      limit: Number.parseInt(searchParams.get('limit') || pageSize.toString()),
      page: Number.parseInt(searchParams.get('page') || '1'),
      ...(searchParams.get('order') && {
        order: searchParams.get('order') || undefined,
      }),
      filter: (() => {
        const filters: Record<string, any> = {};
        const processedKeys = new Set<string>();

        searchParams.forEach((_, key) => {
          if (
            key !== 'limit' &&
            key !== 'page' &&
            key !== 'order' &&
            !processedKeys.has(key)
          ) {
            processedKeys.add(key);
            const values = searchParams.getAll(key);
            if (values.length > 1) {
              filters[key] = values;
            } else {
              const numericValue = Number(values[0]);
              filters[key] =
                !isNaN(numericValue) && values[0] !== ''
                  ? numericValue
                  : values[0];
            }
          }
        });
        return Object.keys(filters).length > 0 ? filters : {};
      })(),
    }),
    [searchParams, pageSize]
  );

  const setCurrentFilter = (
    updater: (prev: PageLimitFilter) => PageLimitFilter
  ) => {
    const newFilter = updater(currentFilter);
    const params = new URLSearchParams(searchParams);

    if (newFilter.limit !== undefined) {
      params.set('limit', newFilter.limit.toString());
    }
    if (newFilter.page !== undefined) {
      params.set('page', newFilter.page.toString());
    }
    if (newFilter.order) {
      params.set('order', newFilter.order);
    } else {
      params.delete('order');
    }

    searchParams.forEach((_, key) => {
      if (key !== 'limit' && key !== 'page' && key !== 'order') {
        params.delete(key);
      }
    });

    if (newFilter.filter && Object.keys(newFilter.filter).length > 0) {
      Object.entries(newFilter.filter).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          return;
        }

        if (key === 'status' && value === '') {
          params.set(key, '');
          return;
        }

        if (value !== '') {
          //handle array values (e.g., for multiple select filters)
          if (Array.isArray(value)) {
            value.forEach(val => {
              params.append(key, String(val));
            });
          } else {
            params.set(key, String(value));
          }
        }
      });
    }
    setSearchParams(params);
  };

  const handleChangeFilter = (filters: Record<string, any>) => {
    setCurrentFilter(prevFilter => {
      return {
        ...prevFilter,
        filter: filters,
        ...(JSON.stringify(prevFilter.filter) === JSON.stringify(filters)
          ? {}
          : { page: 1 }),
      };
    });
  };

  const handleChangeOrder = ({ key }: Pick<TableColumnType<T>, 'key'>) => {
    if (!key) return;

    const column = String(key);
    if (!orderColumn || orderColumn.column !== column) {
      setOrderColumn({ column, inverse: false });
      return;
    }

    if (orderColumn.inverse) {
      setOrderColumn(null);
      return;
    }

    setOrderColumn({ column, inverse: true });
  };

  const handleChangePagination = (take: number, _: any, pagination: number) => {
    setCurrentFilter(prevFilter => {
      return {
        ...prevFilter,
        limit: take,
        page: prevFilter.limit && take !== prevFilter.limit ? 1 : pagination,
      };
    });
  };

  return {
    currentFilter,
    orderColumn,
    tableColumnSortOrderSymbol,
    handleChangeOrder,
    handleChangePagination,
    handleChangeFilter,
  };
}
